import {
  DataGrid,
  GridRowsProp,
  MuiEvent,
  GridCellParams,
  GridRowId,
  GridColumnHeaderParams,
  GridPaginationModel,
  GridFilterModel,
} from "@mui/x-data-grid";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { BiRefresh } from "react-icons/bi";

import { detailRecordProps, historyProps } from "../../@types/assetTypes/ap";
import InitiateInvoiceApi from "../../API/InvoicesApi";
import ResponseAPI from "../../API/index";
import StatusTitle from "../../components/ColumnHeaders/StatusTitle";
import ConfirmModalChild from "../../components/ConfirmModalChild";
import DetailWindow from "../../components/DetailpageTable";
import ConfirmModal from "../../components/Modalwindow";
import Refresh from "../../components/RefreshButton";
import SectionContainer from "../../components/SectionConatiner";
import APAndARStatuses from "../../data/APAndARStatuses.json";
import statusDetail from "../../data/ARNonVMSDetail.json";
import useStore from "../../store/useStore";
import { downloadFile } from "../AP/Ap.logichandler";
import { columns as _columns } from "../AP/Historysection";
import Empty from "../S4Upload/components/Empty";
import Toolbar from "../S4Upload/components/Toolbar";

const columns = _columns.map((c) => {
  if (c?.field === "status")
    return {
      ...c,
      renderHeader: (params: GridColumnHeaderParams) => {
        return (
          <StatusTitle title={params.colDef.headerName} detail={statusDetail} />
        );
      },
      valueOptions: APAndARStatuses,
    };
  if (c?.field === "reInitiate")
    return {
      ...c,
      headerName: "Re-Run",
      width: 80,
      renderCell: () => {
        return <BiRefresh className="h-7 w-7 rotate-90 text-[#211551]" />;
      },
    };

  return c;
});

const Historysection: React.FC<historyProps> = ({
  runHistory,
  totalRowCount,
  paginationModel,
  pageNo,
  pageSize,
  filterModel,
  sortModel,
  getRunHistoryFunc,
  onPaginationModelChange,
  onFilterChange,
  onSortModelChange,
}) => {
  const [totalRowCountDetails, setTotalRowCountDetails] = useState<number>(0);

  const [paginationModelDetails, setPaginationModelDetails] =
    useState<GridPaginationModel>({
      page: 0,
      pageSize: 20,
    });

  const [issuesFilter, setIssuesFilter] = useState<string>("all");
  const enableLoader = useStore((state) => state.enableLoader);
  const setPopup = useStore((state) => state.setPopup);
  const setLoader = useStore((state) => state.setLoader);
  const [processId, setProcessId] = useState<GridRowId>("");
  const [rerunProcessId, setRerunProcessId] = useState<GridRowId>("");
  const [detailData, setDetailData] = useState<detailRecordProps[]>([]);
  const convertData = useStore((state) => state.convertData);
  const enableToast = useStore((state) => state.enableToast);
  const enablePopup = useStore((state) => state.enablePopup);
  const [showModal, setShowModal] = useState<string>("");
  const [rowCountState, setRowCountState] = React.useState(totalRowCount || 0);

  const [detailsFilterModel, setDetailsFilterModel] = useState<GridFilterModel>(
    {
      items: [],
    },
  );

  const issuesFilterRef = useRef(issuesFilter);
  const detailsFilterModelRef = useRef(detailsFilterModel);

  const getDetailData = useCallback(
    (
      runID: GridRowId,
      _issuesFilter?: string,
      _filterModel?: GridFilterModel,
      isOnlyFetchAllRecords?: boolean,
    ): Promise<detailRecordProps[]> => {
      const filterIssues = _issuesFilter ?? issuesFilterRef.current;
      const fModel = _filterModel ?? detailsFilterModelRef.current;

      const remarksFilter = fModel?.items?.find(
        ({ field }) => field === "remarks",
      );

      const filterRemarks: string = [undefined, null, ""].includes(
        remarksFilter?.value,
      )
        ? ""
        : remarksFilter?.value;

      const fIssues =
        filterIssues && filterIssues !== "all"
          ? `errorRecords in (${filterIssues === "issues"})`
          : "";

      const fRemarks = filterRemarks ? `remarks in (${filterRemarks})` : "";

      const pageInfo = {
        pageNo: paginationModelDetails.page,
        pageSize: paginationModelDetails.pageSize,
      };

      const getRequest = InitiateInvoiceApi.getDetailRecord(
        runID,
        { fIssues, fRemarks },
        isOnlyFetchAllRecords ? undefined : pageInfo,
      );

      return ResponseAPI.performRequest(getRequest).then((getResponse) => {
        const { records = [], totalCount = 0 } =
          getResponse?.data?.data?.process?.executionDetail || {};

        if (!isOnlyFetchAllRecords) {
          setDetailData(records);
          setIssuesFilter(filterIssues);
          setDetailsFilterModel(fModel);
          setTotalRowCountDetails(totalCount);
          setPopup(true);
        }

        return records;
      });
    },
    [paginationModelDetails.page, paginationModelDetails.pageSize],
  );

  const reInititateRunHistory = async (runID: GridRowId): Promise<void> => {
    setLoader(true);

    try {
      const getRequest = InitiateInvoiceApi.reInitiateArNonVMSInvoice(runID);
      const getResponse = await ResponseAPI.performRequest(getRequest);

      if (getResponse.data.errors.length !== 0) {
        setLoader(false);
        enableToast({
          message: getResponse.data.errors[0].message,
          type: "error",
        });

        return;
      }

      setLoader(false);
      const newRunID: number = getResponse.data.data.process.executionDetail.id;

      enableToast({
        message: `AR Non-VMS Invoice Re-initiated, Run ID: ${newRunID}`,
        type: "success",
      });
      getRunHistoryFunc(0);
    } catch (error) {
      console.log(error);
      enableToast({
        message: "Network error",
        type: "error",
      });
    }

    setLoader(false);
  };

  const rows: GridRowsProp = runHistory.map((item) => {
    let col6 = "";

    if (["STEP 3/3 COMPLETED"].includes(item.status)) {
      if (item.errorExist) {
        col6 = "Link to report with issues";
      } else {
        col6 = "Link to Report";
      }
    }

    const isErrorStatus = item?.status?.toLowerCase()?.includes("fail");

    return {
      id: item.id,
      col1:
        Object.keys(item.includedInvoices).length > 0
          ? convertData(item.includedInvoices)
          : "",
      col2:
        Object.keys(item.excludedInvoices).length > 0
          ? convertData(item.excludedInvoices)
          : "",
      status: [
        isErrorStatus ? "ERROR" : "-",
        item.status,
        isErrorStatus ? item.remarks : "",
      ]
        .filter(Boolean)
        .join(":"),
      outputFileLocation: ["STEP 3/3 COMPLETED"].includes(item.status)
        ? item.outputFileLocation
        : "",
      startedAt: item?.startedAt?.value || "",
      col6,
      startedBy: item.startedBy,
    };
  });

  const handleClick = (
    params: GridCellParams,
    event: MuiEvent<React.MouseEvent>,
  ): void => {
    event.defaultMuiPrevented = true;
    const { field, id, row } = params;
    const value = row[field];

    if (field === "outputFileLocation" && value) {
      const paramLength = value.split(",").length;
      const fileName = value.split("/").pop();

      downloadFile(id, paramLength === 1 ? fileName : `${id}_NON_VMS.zip`);
    }

    if (field === "reInitiate") {
      setRerunProcessId(id);

      setShowModal("rerun");
    }

    if (field === "col6" && value) {
      setProcessId(id);
    }
  };

  useEffect(() => {
    issuesFilterRef.current = issuesFilter;
  }, [issuesFilter]);

  useEffect(() => {
    detailsFilterModelRef.current = detailsFilterModel;
  }, [detailsFilterModel]);

  useEffect(() => {
    setRowCountState((prevRowCountState) =>
      totalRowCount !== undefined ? totalRowCount : prevRowCountState,
    );
  }, [totalRowCount, setRowCountState]);

  useEffect(() => {
    if (processId) {
      setLoader(true);
      getDetailData(processId)
        .then(() => {
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
    }
  }, [processId, getDetailData]);

  return (
    <>
      <SectionContainer
        title="Accounts Receivable Processor Run History"
        className="w-full mx-auto mt-14 px-10"
      >
        <div className="flex justify-end">
          <Refresh refresh={() => getRunHistoryFunc(pageNo)} label="Refresh" />
        </div>
        <div
          className={[
            rowCountState < pageSize
              ? "max-h-[850px]"
              : pageSize === 5
                ? "min-h-[350px] max-h-[375px] h-[60vh] 2xl:h-[45vh]"
                : "min-h-[450px] max-h-[625px] h-[70vh] 2xl:h-[65vh]",
            "mx-auto my-5 border border-[#211551] rounded-md",
          ].join(" ")}
        >
          <DataGrid
            autoHeight={rowCountState < pageSize}
            hideFooter={rowCountState < pageSize}
            loading={enableLoader}
            sortingOrder={["desc", "asc"]}
            initialState={{
              sorting: {
                sortModel: [{ field: "id", sort: "desc" }],
              },
            }}
            rowCount={rowCountState}
            rows={rows}
            columns={columns}
            slots={{ toolbar: Toolbar, loadingOverlay: Empty }}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            pageSizeOptions={[5, 10]}
            pagination
            paginationMode="server"
            filterMode="server"
            sortingMode="server"
            filterModel={filterModel}
            sortModel={sortModel}
            onFilterModelChange={onFilterChange}
            onSortModelChange={onSortModelChange}
            sx={{
              "& .MuiDataGrid-row:last-child > .MuiDataGrid-cell": {
                borderBottomWidth: 0,
              },
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  cursor: "pointer",
                },
              },
              "& .MuiDataGrid-toolbarContainer": {
                "& .MuiButtonBase-root": { color: "#211551" },
              },

              "& .MuiDataGrid-columnHeaders": {
                "--DataGrid-containerBackground": "#211551",
                bgcolor: "#211551",
                color: "white",
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                },
                "& .MuiIconButton-sizeSmall": {
                  color: "white",
                },
              },
              "& .MuiDataGrid-row": { fontSize: "small" },
              "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                py: "4px",
              },
              "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                py: "10px",
              },
              "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                py: "17px",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader:focus, & MuiDataGrid-columnHeader:focus-within":
                {
                  outline: "none",
                },
            }}
            getRowHeight={() => "auto"}
            isCellEditable={() => false}
            columnHeaderHeight={44}
            disableRowSelectionOnClick
            onCellClick={handleClick}
          />
        </div>
      </SectionContainer>
      {enablePopup && (
        <ConfirmModal>
          <DetailWindow
            records={detailData}
            processId={processId}
            totalRowCount={totalRowCountDetails}
            paginationModel={paginationModelDetails}
            filterModel={detailsFilterModel}
            issuesFilter={issuesFilter}
            onPaginationModelChange={setPaginationModelDetails}
            getDetailData={() =>
              getDetailData(processId, "all", undefined, true)
            }
            onFilterChange={(filterModel) => {
              setLoader(true);
              getDetailData(processId, undefined, filterModel, false)
                .then(() => {
                  setLoader(false);
                })
                .catch(() => {
                  setLoader(false);
                });
            }}
            onIssuesFilterChange={(issuesFilter) => {
              setLoader(true);
              getDetailData(processId, issuesFilter, undefined, false)
                .then(() => {
                  setLoader(false);
                })
                .catch(() => {
                  setLoader(false);
                });
            }}
            onClose={() => {
              setTotalRowCountDetails(0);
              setPaginationModelDetails({ page: 0, pageSize: 20 });
              setIssuesFilter("all");
              setDetailsFilterModel({
                items: [],
              });
              setPopup(false);
              setDetailData([]);
              setProcessId("");
            }}
          />
        </ConfirmModal>
      )}
      {showModal === "rerun" && (
        <ConfirmModal closeModal={() => setShowModal("")}>
          <ConfirmModalChild
            title={`Would you like to re-run Invoice Run Number - ${rerunProcessId}`}
            onYesClick={() => {
              setShowModal("");
              reInititateRunHistory(rerunProcessId);
            }}
            onNoClick={() => setShowModal("")}
          />
        </ConfirmModal>
      )}
    </>
  );
};

export default Historysection;
